import { ByteType, FloatType, HalfFloatType, IntType, ShortType, Texture, UnsignedByteType, UnsignedInt101111Type, UnsignedInt248Type, UnsignedInt5999Type, UnsignedIntType, UnsignedShort4444Type, UnsignedShort5551Type, UnsignedShortType } from "three";
import { isCompressedTexture } from "./helper.js";

// #region Texture Size

export interface TextureMemoryEstimate {
    /** CPU memory usage in bytes. 0 if texture data is not retained on CPU. */
    cpu: number;
    /** GPU VRAM usage in bytes. */
    vram: number;
}

/**
 * Estimates the memory usage of a texture on both CPU and GPU (VRAM).
 *
 * For compressed textures, calculates the size based on block compression.
 * For uncompressed textures, calculates based on pixel format, data type, and channel count.
 * Includes mipmaps in the estimation when present or when generateMipmaps is enabled.
 *
 * CPU memory is only counted if the texture actually retains the image data on the CPU side.
 * This is typically the case for:
 * - Compressed textures (mipmaps stored in CPU memory)
 * - Textures that haven't been uploaded yet
 * - DataTextures with explicit data arrays
 *
 * @param texture The Three.js texture to estimate memory for
 * @returns An object containing CPU and VRAM memory estimates in bytes
 */
export function estimateTextureMemory(texture: Texture): TextureMemoryEstimate {
    let cpu = 0;
    let vram = 0;

    // Check if texture data is still on CPU
    // For regular textures, once uploaded to GPU, the image data may still be referenced
    // For compressed textures, mipmaps are stored in CPU memory
    const hasDataOnCPU = isCompressedTexture(texture) ||
        (texture.image && !texture.isRenderTargetTexture) ||
        ('data' in texture && texture.data);

    const width = texture.image?.width;
    const height = texture.image?.height;

    if (isCompressedTexture(texture)) {
        // Compressed texture memory size estimation
        for (const mip of texture.mipmaps) {
            const mipWidth = mip.width;
            const mipHeight = mip.height;

            // Determine block size based on compression format
            // Common formats: DXT1/BC1 = 8 bytes, DXT3/DXT5/BC2/BC3 = 16 bytes
            // ASTC varies, ETC1/ETC2 = 8 bytes, PVRTC varies
            let blockSize = 16; // Default to DXT5/BC3 (most common)

            // Try to detect format from the texture's format property or mipmap data
            // This is a best-effort estimation since Three.js doesn't always expose the exact format
            if (mip.data && mip.data.byteLength) {
                const numBlocksWide = Math.max(1, Math.floor((mipWidth + 3) / 4));
                const numBlocksHigh = Math.max(1, Math.floor((mipHeight + 3) / 4));
                const totalBlocks = numBlocksWide * numBlocksHigh;
                // Try to infer block size from actual data size
                const inferredBlockSize = mip.data.byteLength / totalBlocks;
                if (inferredBlockSize === 8 || inferredBlockSize === 16) {
                    blockSize = inferredBlockSize;
                }
            }

            const numBlocksWide = Math.max(1, Math.floor((mipWidth + 3) / 4));
            const numBlocksHigh = Math.max(1, Math.floor((mipHeight + 3) / 4));
            const mipSize = numBlocksWide * numBlocksHigh * blockSize;
            vram += mipSize;
            // Compressed textures always keep mipmaps in CPU memory
            if (hasDataOnCPU) {
                cpu += mipSize;
            }
        }
    } else {
        // Uncompressed texture memory size estimation

        // Get bytes per pixel based on texture type and format
        let bytesPerPixel = 0;

        // Determine channel count based on texture.format
        let channels = 4; // Default RGBA
        if (texture.format !== undefined) {
            // Common Three.js texture formats
            const format = texture.format;
            // RedFormat = 1 channel, RGFormat = 2, RGBFormat = 3, RGBAFormat = 4
            // DepthFormat = 1, DepthStencilFormat = 2, AlphaFormat = 1, LuminanceFormat = 1, LuminanceAlphaFormat = 2
            // Format values: Alpha=1023, Red=1028, RG=1030, RGB=1029, RGBA=1023
            if (format === 1028) channels = 1; // RedFormat
            else if (format === 1030) channels = 2; // RGFormat
            else if (format === 1029) channels = 3; // RGBFormat
            else if (format === 1023) channels = 4; // RGBAFormat or AlphaFormat
            // For other formats, keep default of 4
        }

        switch (texture.type) {
            case UnsignedByteType:
            case ByteType:
                bytesPerPixel = 1 * channels;
                break;
            case ShortType:
            case UnsignedShortType:
            case HalfFloatType:
                bytesPerPixel = 2 * channels;
                break;
            case IntType:
            case UnsignedIntType:
            case FloatType:
                bytesPerPixel = 4 * channels;
                break;
            // Packed formats - these pack all channels into a single value
            case UnsignedShort4444Type: // RGBA packed into 16 bits (4+4+4+4)
            case UnsignedShort5551Type: // RGBA packed into 16 bits (5+5+5+1)
                bytesPerPixel = 2; // Total 2 bytes for all channels
                break;
            case UnsignedInt248Type: // Depth24 + Stencil8 packed into 32 bits
            case UnsignedInt5999Type: // RGB9_E5 packed into 32 bits (shared exponent)
            case UnsignedInt101111Type: // RG11B10F packed into 32 bits
                bytesPerPixel = 4; // Total 4 bytes for all channels
                break;
            default:
                bytesPerPixel = 4 * channels; // Default to 4 bytes per channel
                break;
        }

        if (width && height) {
            // Calculate size for the main texture level
            let totalSize = width * height * bytesPerPixel;

            // Account for mipmaps if they exist
            // Mipmaps add approximately 1/3 more memory (1 + 1/4 + 1/16 + 1/64 + ... ≈ 1.333)
            if (texture.mipmaps && texture.mipmaps.length > 0) {
                // If mipmaps are explicitly provided, calculate their exact size
                for (const mip of texture.mipmaps) {
                    if (mip.width && mip.height) {
                        totalSize += mip.width * mip.height * bytesPerPixel;
                    }
                }
            } else if (texture.generateMipmaps) {
                // If mipmaps will be auto-generated, estimate their size
                totalSize = Math.floor(totalSize * 1.333);
            }

            vram = totalSize;
            // Only count CPU memory if texture data is actually retained
            if (hasDataOnCPU) {
                cpu = totalSize;
            }
        }
    }

    return { cpu, vram };
}

// #endregion



// #region Texture Format Helpers

/*
export type PixelFormat =
    | typeof AlphaFormat
    | typeof RGBFormat
    | typeof RGBAFormat
    | typeof DepthFormat
    | typeof DepthStencilFormat
    | typeof RedFormat
    | typeof RedIntegerFormat
    | typeof RGFormat
    | typeof RGIntegerFormat
    | typeof RGBIntegerFormat
    | typeof RGBAIntegerFormat;

export type DepthTexturePixelFormat = typeof DepthFormat | typeof DepthStencilFormat;

export type CompressedPixelFormat =
    | typeof RGB_S3TC_DXT1_Format
    | typeof RGBA_S3TC_DXT1_Format
    | typeof RGBA_S3TC_DXT3_Format
    | typeof RGBA_S3TC_DXT5_Format
    | typeof RGB_PVRTC_4BPPV1_Format
    | typeof RGB_PVRTC_2BPPV1_Format
    | typeof RGBA_PVRTC_4BPPV1_Format
    | typeof RGBA_PVRTC_2BPPV1_Format
    | typeof RGB_ETC1_Format
    | typeof RGB_ETC2_Format
    | typeof RGBA_ETC2_EAC_Format
    | typeof R11_EAC_Format
    | typeof SIGNED_R11_EAC_Format
    | typeof RG11_EAC_Format
    | typeof SIGNED_RG11_EAC_Format
    | typeof RGBA_ASTC_4x4_Format
    | typeof RGBA_ASTC_5x4_Format
    | typeof RGBA_ASTC_5x5_Format
    | typeof RGBA_ASTC_6x5_Format
    | typeof RGBA_ASTC_6x6_Format
    | typeof RGBA_ASTC_8x5_Format
    | typeof RGBA_ASTC_8x6_Format
    | typeof RGBA_ASTC_8x8_Format
    | typeof RGBA_ASTC_10x5_Format
    | typeof RGBA_ASTC_10x6_Format
    | typeof RGBA_ASTC_10x8_Format
    | typeof RGBA_ASTC_10x10_Format
    | typeof RGBA_ASTC_12x10_Format
    | typeof RGBA_ASTC_12x12_Format
    | typeof RGBA_BPTC_Format
    | typeof RGB_BPTC_SIGNED_Format
    | typeof RGB_BPTC_UNSIGNED_Format
    | typeof RED_RGTC1_Format
    | typeof SIGNED_RED_RGTC1_Format
    | typeof RED_GREEN_RGTC2_Format
    | typeof SIGNED_RED_GREEN_RGTC2_Format;
    */


export function getTexturePixelFormatAsString(format: number): string | undefined {

    // AnyPixelFormat
    switch (format) {
        case 1023: return "RGBAFormat";
        case 1028: return "RedFormat";
        case 1030: return "RGFormat";
        case 1029: return "RGBFormat";
        case 1024: return "AlphaFormat";
        case 1025: return "DepthFormat";
        case 1026: return "DepthStencilFormat";
        // CompressedPixelFormats
        case 33776: return "RGB_S3TC_DXT1_Format";
        case 33777: return "RGBA_S3TC_DXT1_Format";
        case 33778: return "RGBA_S3TC_DXT3_Format";
        case 33779: return "RGBA_S3TC_DXT5_Format";
        case 35840: return "RGB_PVRTC_4BPPV1_Format";
        case 35841: return "RGB_PVRTC_2BPPV1_Format";
        case 35842: return "RGBA_PVRTC_4BPPV1_Format";
        case 35843: return "RGBA_PVRTC_2BPPV1_Format";
        case 36196: return "RGB_ETC1_Format";
        case 37492: return "RGB_ETC2_Format";
        case 37496: return "RGBA_ETC2_EAC_Format";
        case 37488: return "R11_EAC_Format";
        case 37489: return "SIGNED_R11_EAC_Format";
        case 37490: return "RG11_EAC_Format";
        case 37491: return "SIGNED_RG11_EAC_Format";
        case 37808: return "RGBA_ASTC_4x4_Format";
        case 37809: return "RGBA_ASTC_5x4_Format";
        case 37810: return "RGBA_ASTC_5x5_Format";
        case 37811: return "RGBA_ASTC_6x5_Format";
        case 37812: return "RGBA_ASTC_6x6_Format";
        case 37813: return "RGBA_ASTC_8x5_Format";
        case 37814: return "RGBA_ASTC_8x6_Format";
        case 37815: return "RGBA_ASTC_8x8_Format";
        case 37816: return "RGBA_ASTC_10x5_Format";
        case 37817: return "RGBA_ASTC_10x6_Format";
        case 37818: return "RGBA_ASTC_10x8_Format";
        case 37819: return "RGBA_ASTC_10x10_Format";
        case 37820: return "RGBA_ASTC_12x10_Format";
        case 37821: return "RGBA_ASTC_12x12_Format";
        case 36492: return "RGBA_BPTC_Format";
        case 36494: return "RGB_BPTC_SIGNED_Format";
        case 36495: return "RGB_BPTC_UNSIGNED_Format";
        case 36283: return "RED_RGTC1_Format";
        case 36284: return "SIGNED_RED_RGTC1_Format";
        case 36285: return "RED_GREEN_RGTC2_Format";
        case 36286: return "SIGNED_RED_GREEN_RGTC2_Format";
        default: undefined;
    }
}

/*
export type TextureDataType =
    | typeof UnsignedByteType
    | typeof ByteType
    | typeof ShortType
    | typeof UnsignedShortType
    | typeof IntType
    | typeof UnsignedIntType
    | typeof FloatType
    | typeof HalfFloatType
    | typeof UnsignedShort4444Type
    | typeof UnsignedShort5551Type
    | typeof UnsignedInt248Type
    | typeof UnsignedInt5999Type
    | typeof UnsignedInt101111Type;
    
    
export const UnsignedByteType: 1009;
export const ByteType: 1010;
export const ShortType: 1011;
export const UnsignedShortType: 1012;
export const IntType: 1013;
export const UnsignedIntType: 1014;
export const FloatType: 1015;
export const HalfFloatType: 1016;
export const UnsignedShort4444Type: 1017;
export const UnsignedShort5551Type: 1018;
export const UnsignedInt248Type: 1020;
export const UnsignedInt5999Type: 35902;
export const UnsignedInt101111Type: 35899;
    
*/

export function getTextureDataTypeAsString(type: number): string | undefined {
    switch (type) {
        case 1009: return "UnsignedByteType";
        case 1010: return "ByteType";
        case 1011: return "ShortType";
        case 1012: return "UnsignedShortType";
        case 1013: return "IntType";
        case 1014: return "UnsignedIntType";
        case 1015: return "FloatType";
        case 1016: return "HalfFloatType";
        case 1017: return "UnsignedShort4444Type";
        case 1018: return "UnsignedShort5551Type";
        case 1020: return "UnsignedInt248Type";
        case 35902: return "UnsignedInt5999Type";
        case 35899: return "UnsignedInt101111Type";
        default: return undefined;
    }
}